import maplibregl from 'maplibre-gl';
import store from '../state';
import { getTileUrl } from '../config';
import { webcamModal } from './panels/WebcamModal';
import * as pmtiles from 'pmtiles';
import { SolarLunarService } from '../services/SolarLunarService';

export interface WebcamConfig {
  name: string;
  lat: number;
  lon: number;
  type: string;
  url: string;
}

// Convert latitude to Tile Y index
function lat2tile(lat: number, zoom: number): number {
  return Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
}

// Convert longitude to Tile X index
function lng2tile(lon: number, zoom: number): number {
  return Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
}

interface WebcamMarkerItem {
  cam: WebcamConfig;
  marker: maplibregl.Marker;
  el: HTMLElement;
  inner: HTMLElement;
}

export class WebcamManager {
  private map: maplibregl.Map;
  private markers: maplibregl.Marker[] = [];
  private markerItems: WebcamMarkerItem[] = [];
  private webcams: WebcamConfig[] = [];
  private enabled: boolean = false;
  private currentMarkersProduct: string | null = null;
  private currentMarkersDate: string | null = null;
  private currentMarkersHour: string | null = null;
  private activePopup: maplibregl.Popup | null = null;
  private pmtilesCache: Map<string, pmtiles.PMTiles> = new Map();
  private calendarWidgetEl: HTMLElement | null = null;
  private activeSpiderCluster: WebcamMarkerItem[] = [];
  private spiderCollapseTimer: number | null = null;

  constructor(map: maplibregl.Map) {
    this.map = map;
    this.loadWebcams();
    this.createCalendarWidget();

    this.map.on('movestart', () => this.collapseSpider());

    store.subscribe((state) => {
      const isTestMode = state.manifest?.mode === 'test';
      // Enable whenever webcam validator button is active in test mode (no layer restrictions)
      const shouldBeEnabled = isTestMode && state.webcamsEnabled;

      const dateStr = state.selectedWebcamDate || (state.currentTimeStep
        ? state.currentTimeStep.toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]);

      let isMorning = true;
      if (state.currentTimeStep) {
        const tz = state.userTimezone || 'America/Los_Angeles';
        try {
          const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false });
          const localHour = parseInt(dFmt.format(state.currentTimeStep), 10);
          isMorning = (localHour === 24 ? 0 : localHour) < 12;
        } catch {
          isMorning = state.currentTimeStep.getUTCHours() < 12;
        }
      }

      const product = state.primaryProduct;
      let hourStr: string;
      if (product === 'sunrise') {
        hourStr = isMorning ? 'sunrise' : 'sunset';
      } else if (product === 'alpenglow') {
        hourStr = isMorning ? 'alpenglow_morning' : 'alpenglow_evening';
      } else {
        const activeStep = state.timelineRange.find(t => {
          const stepDate = new Date(t.raw || `${t.date}T${t.hour.replace('Z', '').split(':')[0].padStart(2, '0')}:00:00Z`);
          return stepDate.getTime() === (state.currentTimeStep?.getTime() || 0);
        });
        hourStr = activeStep ? activeStep.hour : '';
      }

      this.updateCalendarWidgetVisibility(shouldBeEnabled, dateStr);

      if (
        shouldBeEnabled !== this.enabled ||
        (shouldBeEnabled && (
          state.primaryProduct !== this.currentMarkersProduct ||
          dateStr !== this.currentMarkersDate ||
          hourStr !== this.currentMarkersHour
        ))
      ) {
        this.enabled = shouldBeEnabled;
        this.currentMarkersProduct = state.primaryProduct;
        this.currentMarkersDate = dateStr;
        this.currentMarkersHour = hourStr;
        this.refreshMarkers(dateStr, hourStr);
      }
    });
  }

  private createCalendarWidget() {
    const mapContainer = this.map.getContainer();
    if (!mapContainer) return;

    this.calendarWidgetEl = document.createElement('div');
    this.calendarWidgetEl.id = 'gw-webcam-calendar-widget';
    this.calendarWidgetEl.className = 'gw-webcam-calendar-widget';
    this.calendarWidgetEl.style.display = 'none';

    mapContainer.appendChild(this.calendarWidgetEl);
  }

  private updateCalendarWidgetVisibility(visible: boolean, selectedDate: string) {
    if (!this.calendarWidgetEl) return;
    if (!visible) {
      this.calendarWidgetEl.style.display = 'none';
      return;
    }

    this.calendarWidgetEl.style.display = 'flex';
    this.calendarWidgetEl.innerHTML = `
      <div class="gw-webcam-calendar-header">
        <div style="display: flex; align-items: center; gap: 6px;">
          <span style="font-size: 14px;">📸</span>
          <span style="font-weight: 700; font-size: 12px; color: #f8fafc; letter-spacing: 0.5px;">WEBCAM VALIDATOR</span>
        </div>
        <span style="font-size: 10px; color: #94a3b8;">${this.webcams.length} Cameras</span>
      </div>
      <div class="gw-webcam-calendar-controls">
        <button id="gw-webcam-prev-day" class="gw-webcam-cal-btn" title="Previous Day">◀</button>
        <input type="date" id="gw-webcam-date-input" class="gw-webcam-date-input" value="${selectedDate}">
        <button id="gw-webcam-next-day" class="gw-webcam-cal-btn" title="Next Day">▶</button>
        <button id="gw-webcam-today-btn" class="gw-webcam-cal-btn gw-webcam-today-btn" title="Jump to Today">Today</button>
      </div>
      <div style="font-size: 10px; color: #64748b; line-height: 1.2;">
        Select past date & click any marker to view captures.
      </div>
    `;

    const dateInput = this.calendarWidgetEl.querySelector('#gw-webcam-date-input') as HTMLInputElement;
    const prevBtn = this.calendarWidgetEl.querySelector('#gw-webcam-prev-day') as HTMLButtonElement;
    const nextBtn = this.calendarWidgetEl.querySelector('#gw-webcam-next-day') as HTMLButtonElement;
    const todayBtn = this.calendarWidgetEl.querySelector('#gw-webcam-today-btn') as HTMLButtonElement;

    const handleDateSelection = (newDateStr: string) => {
      const state = store.getState();
      const targetDate = new Date(`${newDateStr}T12:00:00Z`);
      const tz = state.userTimezone || 'America/Los_Angeles';
      const daySteps = SolarLunarService.generateDayTimeline(targetDate, tz);
      const firstStep = daySteps[0] ? new Date(daySteps[0].raw) : targetDate;

      store.update({
        selectedWebcamDate: newDateStr,
        timelineRange: daySteps,
        currentTimeStep: firstStep
      });
    };

    if (dateInput) {
      dateInput.addEventListener('change', (e) => {
        e.stopPropagation();
        if (dateInput.value) {
          handleDateSelection(dateInput.value);
        }
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = new Date(`${selectedDate}T12:00:00Z`);
        current.setDate(current.getDate() - 1);
        const newDateStr = current.toISOString().split('T')[0];
        handleDateSelection(newDateStr);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = new Date(`${selectedDate}T12:00:00Z`);
        current.setDate(current.getDate() + 1);
        const newDateStr = current.toISOString().split('T')[0];
        handleDateSelection(newDateStr);
      });
    }

    if (todayBtn) {
      todayBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const todayStr = new Date().toISOString().split('T')[0];
        handleDateSelection(todayStr);
      });
    }
  }

  private async loadWebcams() {
    try {
      // Fetch webcams.json list with cache-busting timestamp
      const resWebcams = await fetch(`webcams/webcams.json?_t=${Date.now()}`);
      if (resWebcams.ok) {
        this.webcams = await resWebcams.json();
        console.log(`📸 WebcamManager: Loaded ${this.webcams.length} webcams.`);
        // Refresh calendar widget with camera count if already visible
        const state = store.getState();
        const dateStr = state.selectedWebcamDate || new Date().toISOString().split('T')[0];
        this.updateCalendarWidgetVisibility(this.enabled, dateStr);
        if (this.enabled) {
          this.refreshMarkers(this.currentMarkersDate || dateStr, this.currentMarkersHour || '');
        }
      }
    } catch (e) {
      console.warn('⚠️ WebcamManager: Failed to fetch webcam configurations.', e);
    }
  }

  private expandSpider(hoveredItem: WebcamMarkerItem) {
    if (this.spiderCollapseTimer) {
      window.clearTimeout(this.spiderCollapseTimer);
      this.spiderCollapseTimer = null;
    }

    const posH = this.map.project([hoveredItem.cam.lon, hoveredItem.cam.lat]);

    // Find colocated / overlapping markers (within 0.08 deg geographic delta and 28px screen distance)
    const nearby = this.markerItems.filter(item => {
      const isColocated =
        Math.abs(item.cam.lat - hoveredItem.cam.lat) <= 0.08 &&
        Math.abs(item.cam.lon - hoveredItem.cam.lon) <= 0.08;
      if (!isColocated) return false;
      const p = this.map.project([item.cam.lon, item.cam.lat]);
      return Math.hypot(p.x - posH.x, p.y - posH.y) <= 28;
    });

    if (nearby.length <= 1) {
      this.collapseSpider();
      hoveredItem.inner.style.transform = 'scale(1.3)';
      hoveredItem.inner.style.boxShadow = '0 0 10px #00e5ff, 0 2px 8px rgba(0,0,0,0.6)';
      hoveredItem.inner.style.backgroundColor = '#ffffff';
      hoveredItem.el.style.zIndex = '50';
      return;
    }

    // Collapse any previous cluster if it does not match
    if (
      this.activeSpiderCluster.length > 0 &&
      (this.activeSpiderCluster.length !== nearby.length || !nearby.every(n => this.activeSpiderCluster.includes(n)))
    ) {
      this.collapseSpider();
    }

    this.activeSpiderCluster = nearby;
    const n = nearby.length;
    const radius = Math.max(26, 18 + n * 4);

    nearby.forEach((item, idx) => {
      const angle = (2 * Math.PI * idx) / n - Math.PI / 2;
      const dx = Math.round(Math.cos(angle) * radius);
      const dy = Math.round(Math.sin(angle) * radius);

      const isCurrent = item === hoveredItem;
      const scale = isCurrent ? 1.35 : 1.15;
      item.inner.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
      item.inner.style.boxShadow = isCurrent
        ? '0 0 12px #00e5ff, 0 4px 10px rgba(0,0,0,0.7)'
        : '0 2px 8px rgba(0,0,0,0.6)';
      item.inner.style.backgroundColor = isCurrent ? '#ffffff' : '#00c8ff';
      item.el.style.zIndex = isCurrent ? '100' : '50';
    });
  }

  private collapseSpider() {
    if (this.spiderCollapseTimer) {
      window.clearTimeout(this.spiderCollapseTimer);
      this.spiderCollapseTimer = null;
    }
    if (this.activeSpiderCluster.length > 0) {
      this.activeSpiderCluster.forEach(item => {
        item.inner.style.transform = 'translate(0px, 0px) scale(1)';
        item.inner.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
        item.inner.style.backgroundColor = '#00c8ff';
        item.el.style.zIndex = '1';
      });
      this.activeSpiderCluster = [];
    }
    this.markerItems.forEach(item => {
      if (!this.activeSpiderCluster.includes(item)) {
        item.inner.style.transform = 'translate(0px, 0px) scale(1)';
        item.inner.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
        item.inner.style.backgroundColor = '#00c8ff';
        item.el.style.zIndex = '1';
      }
    });
  }

  private refreshMarkers(dateStr: string, hourStr: string) {
    this.collapseSpider();

    // Clear existing markers
    this.markers.forEach(m => m.remove());
    this.markers = [];
    this.markerItems = [];
    if (this.activePopup) {
      this.activePopup.remove();
      this.activePopup = null;
    }

    if (!this.enabled || this.webcams.length === 0) return;

    const state = store.getState();
    const product = state.primaryProduct; // 'sunrise', 'alpenglow', 'cloud', 'smoke', 'precip', etc.

    let isMorning = true;
    if (state.currentTimeStep) {
      const tz = state.userTimezone || 'America/Los_Angeles';
      try {
        const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false });
        const localHour = parseInt(dFmt.format(state.currentTimeStep), 10);
        isMorning = (localHour === 24 ? 0 : localHour) < 12;
      } catch {
        isMorning = state.currentTimeStep.getUTCHours() < 12;
      }
    }

    const resolvedDateStr = state.selectedWebcamDate || dateStr;
    let event: string;
    if (product === 'sunrise') {
      event = isMorning ? 'sunrise' : 'sunset';
    } else if (product === 'alpenglow') {
      event = isMorning ? 'alpenglow_morning' : 'alpenglow_evening';
    } else {
      event = hourStr || (product || 'forecast');
    }

    // Display all webcams unconditionally when validator is enabled
    const activeWebcams = this.webcams;

    activeWebcams.forEach(cam => {
      const el = document.createElement('div');
      el.className = 'webcam-marker';
      el.style.cursor = 'pointer';
      el.style.zIndex = '1';

      const inner = document.createElement('div');
      inner.className = 'webcam-marker-inner';
      inner.style.width = '14px';
      inner.style.height = '14px';
      inner.style.borderRadius = '50%';
      inner.style.backgroundColor = '#00c8ff';
      inner.style.border = '2px solid #ffffff';
      inner.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
      inner.style.transition = 'transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.2s, background-color 0.2s';
      inner.style.pointerEvents = 'auto';
      el.appendChild(inner);

      const item: WebcamMarkerItem = { cam, marker: null as any, el, inner };

      el.addEventListener('mouseenter', () => {
        this.expandSpider(item);
        this.showTooltip(cam, resolvedDateStr, event);
      });

      el.addEventListener('mouseleave', () => {
        if (this.activeSpiderCluster.length > 0) {
          if (this.spiderCollapseTimer) window.clearTimeout(this.spiderCollapseTimer);
          this.spiderCollapseTimer = window.setTimeout(() => {
            this.collapseSpider();
          }, 300);
        } else {
          inner.style.transform = 'translate(0px, 0px) scale(1)';
          inner.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
          inner.style.backgroundColor = '#00c8ff';
          el.style.zIndex = '1';
        }
        if (this.activePopup) {
          this.activePopup.remove();
          this.activePopup = null;
        }
      });

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        this.collapseSpider();
        this.openWebcamDetails(cam, resolvedDateStr, event);
      });

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([cam.lon, cam.lat])
        .addTo(this.map);

      item.marker = marker;
      this.markers.push(marker);
      this.markerItems.push(item);
    });
  }

  private async showTooltip(cam: WebcamConfig, dateStr: string, event: string) {
    if (this.activePopup) {
      this.activePopup.remove();
    }

    const cleanName = cam.name.replace(/ /g, '_');
    const camType = (cam.type || 'camera').toUpperCase();

    this.activePopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 12,
      maxWidth: '340px',
      className: 'gw-webcam-popup'
    });

    this.activePopup
      .setLngLat([cam.lon, cam.lat])
      .setHTML(`
        <div class="webcam-hover-card">
          <div class="webcam-hover-header">
            <div class="webcam-hover-title-row">
              <span class="webcam-hover-icon">📸</span>
              <b class="webcam-hover-name">${cam.name.replace(/_/g, ' ')}</b>
            </div>
            <div class="webcam-hover-meta-row">
              <span class="webcam-hover-type">${camType}</span>
              <span class="webcam-hover-coords">${cam.lat.toFixed(3)}°, ${cam.lon.toFixed(3)}°</span>
            </div>
          </div>

          <div class="webcam-hover-img-wrapper">
            <img 
              src="webcams/latest/${cleanName}.jpg" 
              alt="${cam.name}" 
              class="webcam-hover-img"
              onerror="if(!this.dataset.triedFallback){this.dataset.triedFallback='1';this.src='webcams/${cleanName}-latest.jpg';}else{this.style.display='none';if(this.nextElementSibling)this.nextElementSibling.style.display='flex';}"
            />
            <div class="webcam-hover-no-img" style="display: none;">
              <span>📷 No recent capture available</span>
            </div>
            <div class="webcam-hover-live-badge">LATEST SCRAPE</div>
          </div>

          <div class="webcam-hover-footer">
            <div id="popup-score-container" class="webcam-hover-score-box">
              <span style="color: #94a3b8; font-size: 11px;">Forecast Score:</span>
              <span class="webcam-score-val" style="background: rgba(255,255,255,0.08); color: #cbd5e1; padding: 2px 6px; border-radius: 4px; font-size: 11px; margin-left: 5px;">...</span>
            </div>
            <span class="webcam-hover-hint">Click for full timeline ↗</span>
          </div>
        </div>
      `)
      .addTo(this.map);

    const score = await this.probeScoreAt(cam.lat, cam.lon, dateStr, event);
    const scoreContainer = document.getElementById('popup-score-container');
    if (scoreContainer && this.activePopup) {
      if (score !== null) {
        let scoreBg = '#3b82f6';
        if (score >= 75) scoreBg = '#ef4444';
        else if (score >= 50) scoreBg = '#f59e0b';
        else if (score >= 25) scoreBg = '#10b981';
        scoreContainer.innerHTML = `
          <span style="color: #94a3b8; font-size: 11px;">Forecast Score:</span>
          <span class="webcam-score-val" style="background: ${scoreBg}; color: #ffffff; font-weight: 700; padding: 2px 7px; border-radius: 4px; font-size: 11px; margin-left: 5px;">${score}</span>
        `;
      } else {
        scoreContainer.innerHTML = `
          <span style="color: #94a3b8; font-size: 11px;">Forecast Score:</span>
          <span class="webcam-score-val" style="background: rgba(255,255,255,0.08); color: #94a3b8; padding: 2px 6px; border-radius: 4px; font-size: 11px; margin-left: 5px;">--</span>
        `;
      }
    }
  }

  private async probeScoreAt(lat: number, lon: number, dateStr: string, event: string): Promise<number | null> {
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return null;

    const product = state.primaryProduct || 'sunrise';
    const model = state.selectedModels[product] || (product === 'sunrise' ? 'blend_50' : 'alpenglow_blend');

    const tileUrlTemplate = getTileUrl(manifest, product as any, model, dateStr, event);
    if (!tileUrlTemplate.startsWith('pmtiles://')) return null;

    const innerUrl = tileUrlTemplate.substring('pmtiles://'.length);
    const pmtilesUrlMatch = innerUrl.match(/^(.*\.pmtiles)/);
    if (!pmtilesUrlMatch) return null;

    const pmtilesUrl = new URL(pmtilesUrlMatch[1], window.location.href).href;

    try {
      let instance = this.pmtilesCache.get(pmtilesUrl);
      if (!instance) {
        instance = new pmtiles.PMTiles(pmtilesUrl);
        this.pmtilesCache.set(pmtilesUrl, instance);
      }

      const zoom = 4;
      const tx = lng2tile(lon, zoom);
      const ty = lat2tile(lat, zoom);

      const response = await instance.getZxy(zoom, tx, ty);
      if (!response) return null;

      const blob = new Blob([response.data], { type: 'image/webp' });
      const url = URL.createObjectURL(blob);

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          const w = img.width || 256;
          const h = img.height || 256;
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) {
            resolve(null);
            return;
          }

          const worldDim = Math.pow(2, zoom) * w;
          const pixelX = Math.floor(((lon + 180) / 360) * worldDim) % w;
          const sinLat = Math.sin(lat * Math.PI / 180);
          const pixelY = Math.floor((0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * worldDim) % h;

          ctx.drawImage(img, 0, 0, w, h);
          const pData = ctx.getImageData(pixelX, pixelY, 1, 1).data;
          const r = pData[0];
          const g = pData[1];
          const a = pData[3];

          if (a === 0) {
            resolve(null);
            return;
          }

          let scoreVal = 0;
          if (g < r) {
            scoreVal = 100 - (g / 255 * 50);
          } else {
            scoreVal = (r - 110) / 145 * 50;
          }
          resolve(Math.round(Math.max(0, scoreVal)));
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };
        img.src = url;
      });
    } catch (e) {
      console.warn("Error probing forecast score:", e);
      return null;
    }
  }

  private async openWebcamDetails(cam: WebcamConfig, dateStr: string, event: string) {
    const score = await this.probeScoreAt(cam.lat, cam.lon, dateStr, event);
    webcamModal.open(cam, dateStr, event, score);
  }
}
